import { Button } from "antd";
import { Icon, IconLike } from "components/Icon/Icon";
import PuffLoader from "react-spinners/PuffLoader";
import { opensInNewTab } from "utils/service";
import "./IconButton.scss";

type IconButtonProps = {
    icon: IconLike;
    onClick?: (e: any) => void;
    /**
     * When the action goes to an addressable place, pass its URL: the icon
     * then renders as a real link (`<a href>`), so middle-click / Ctrl-click
     * open it in a new tab and right-click offers "copy link address".
     * A plain left-click still runs `onClick` (e.g. an in-app `history.push`)
     * instead of a full page load. Without `onClick` the browser follows the
     * link natively (use `target="_blank"` for external pages).
     */
    href?: string;
    target?: string;
    className?: String;
    loading?: Boolean;
    disabled?: boolean;
    onMouseEnter?: (e) => void;
    onMouseLeave?: (e) => void;
    "data-testid"?: string;
    "aria-label"?: string;
};

/**
 * Click handler for an icon link: a new-tab click (modifier key or middle
 * button) is left to the browser; a plain left-click is routed to `onClick`
 * and the native navigation is suppressed so the SPA navigates in place.
 */
export const handleIconLinkClick = (e: any, onClick?: (e: any) => void) => {
    if (!onClick || opensInNewTab(e)) {
        return;
    }
    e.preventDefault();
    onClick(e);
};

const IconButton = ({
    icon,
    onClick,
    href,
    target,
    className = "",
    loading = false,
    ...props
}: IconButtonProps) => (
    <div
        className={`${className} iconButtonContainer ${
            props?.disabled ? "iconButtonContainerDisabled" : ""
        }`}
    >
        {href ? (
            <Button
                className="iconButton"
                href={href}
                target={target}
                rel={target === "_blank" ? "noopener noreferrer" : undefined}
                onClick={(e) => handleIconLinkClick(e, onClick)}
                {...props}
            >
                <Icon icon={icon} />
            </Button>
        ) : (
            <Button className="iconButton" onClick={onClick ?? (() => {})} {...props}>
                <Icon icon={icon} />
            </Button>
        )}
        {loading && (
            <div className="loadingAnimation">
                <PuffLoader loading size={40} color="#56a8c2" speedMultiplier={2} />
            </div>
        )}
    </div>
);

export default IconButton;
